import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Form,
  Loader,
  Dimmer,
  Label,
  List,
  Icon,
} from "semantic-ui-react";
import { fetchUserAttributes } from "@aws-amplify/auth";
import axios from "../../../apis/dvdlocator";
import { renderDate } from "../../../formatting/dateFormat";
import { normalizeIdentifierInput } from "./listingPayload";

interface AsinListing {
  listing_id: string;
  msku: string | null;
  listing_title: string | null;
}

interface ProductAsin {
  asin: string;
  created_by: string | null;
  created_at: string | null;
  listings: AsinListing[];
}

interface ProductSearchResult {
  product_id: number;
  product_title: string;
}

interface AddCompetitorAsinToProductProps {
  productId?: number;
  productTitle?: string;
  onClose: (didCreate?: boolean) => void;
}

export function AddCompetitorAsinToProduct({
  productId,
  productTitle,
  onClose,
}: AddCompetitorAsinToProductProps) {
  // Product search state (used when no productId prop is provided)
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ProductSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<
    number | undefined
  >(undefined);
  const [selectedProductTitle, setSelectedProductTitle] = useState("");

  const effectiveProductId = productId ?? selectedProductId;
  const effectiveProductTitle = productTitle || selectedProductTitle;

  const needsSearch = productId === undefined;

  // Debounce the search query
  useEffect(() => {
    if (!needsSearch) return;
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, needsSearch]);

  // Fetch search results
  useEffect(() => {
    if (!needsSearch || !debouncedQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const abortController = new AbortController();
    async function fetchResults() {
      setIsSearching(true);
      try {
        const res = await axios.get("/products", {
          params: { search: debouncedQuery.trim() },
          signal: abortController.signal,
        });
        setSearchResults(res.data ?? []);
      } catch {
        if (!abortController.signal.aborted) setSearchResults([]);
      } finally {
        if (!abortController.signal.aborted) setIsSearching(false);
      }
    }
    fetchResults();
    return () => abortController.abort();
  }, [debouncedQuery, needsSearch]);

  // ASIN loading state
  const [asins, setAsins] = useState<ProductAsin[]>([]);
  const [loadingAsins, setLoadingAsins] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [createdBy, setCreatedBy] = useState("");
  const [createdOn] = useState(() => new Date().toLocaleString());

  useEffect(() => {
    fetchUserAttributes()
      .then((attrs) => {
        if (attrs.name) setCreatedBy(attrs.name);
      })
      .catch((err) => console.log(err));
  }, []);

  const [asin, setAsin] = useState("");
  const [competitorListingTitle, setCompetitorListingTitle] = useState("");
  const [competitorListingTitleTouched, setCompetitorListingTitleTouched] =
    useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Default the competitor listing title to the product title, but only
  // while the user hasn't typed their own value into the field.
  useEffect(() => {
    if (competitorListingTitleTouched) return;
    setCompetitorListingTitle(effectiveProductTitle);
  }, [effectiveProductTitle, competitorListingTitleTouched]);

  useEffect(() => {
    if (!effectiveProductId) return;
    const abortController = new AbortController();

    async function fetchAsins() {
      setLoadingAsins(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${effectiveProductId}/listings`, {
          signal: abortController.signal,
        });
        setAsins(res.data.asins ?? []);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load existing ASINs. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingAsins(false);
      }
    }

    fetchAsins();
    return () => abortController.abort();
  }, [effectiveProductId]);

  const asinTrimmed = asin.trim().toUpperCase();
  const isValidFormat = /^[A-Z0-9]{10}$/.test(asinTrimmed);
  const isDuplicate = asins.some((a) => a.asin.toUpperCase() === asinTrimmed);
  const competitorListingTitleTrimmed = competitorListingTitle.trim();
  const canSubmit =
    isValidFormat &&
    !isDuplicate &&
    !!competitorListingTitleTrimmed &&
    !isSubmitting &&
    !!effectiveProductId;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`/product/${effectiveProductId}/competitor-listing`, {
        asin: asinTrimmed,
        competitor_listing_title: competitorListingTitleTrimmed,
        created_by: createdBy || undefined,
      });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSubmitError("This ASIN is already linked to a product.");
      } else if (err.response?.status === 404) {
        setSubmitError("Product not found.");
      } else {
        setSubmitError(
          err.response?.data?.message || "An error occurred. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose()}
      closeOnDimmerClick={false}
      size="large"
    >
      <Modal.Header>Add Competitor ASIN to Product</Modal.Header>
      <Modal.Content>
        <Message
          info
          style={{
            marginTop: 0,
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
          }}
        >
          <Icon name="info circle" size="big" />
          <Message.Content>
            This is for competitor ASINs only. Do not use for ASINs that YOM
            sells on.
          </Message.Content>
        </Message>

        {/* Product search (only when no productId prop provided) */}
        {needsSearch && !selectedProductId && (
          <>
            <Header as="h4" dividing>
              Search for a Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Search by product title or product ID to select a product.
            </p>
            <Form>
              <Form.Input
                placeholder="Search by title or product ID…"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedProductId(undefined);
                  setSelectedProductTitle("");
                }}
                loading={isSearching}
                icon="search"
                iconPosition="left"
              />
            </Form>
            {searchResults.length > 0 && (
              <List
                divided
                selection
                style={{
                  marginTop: "0.5em",
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
              >
                {searchResults.map((result) => (
                  <List.Item
                    key={result.product_id}
                    onClick={() => {
                      setSelectedProductId(result.product_id);
                      setSelectedProductTitle(result.product_title);
                      setSearchQuery("");
                      setSearchResults([]);
                    }}
                    style={{ padding: "0.75em 1em", cursor: "pointer" }}
                  >
                    <List.Content>
                      <List.Header>
                        <Label size="tiny" horizontal>
                          ID {result.product_id}
                        </Label>
                        {result.product_title}
                      </List.Header>
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
            {debouncedQuery.trim() &&
              !isSearching &&
              searchResults.length === 0 && (
                <Segment textAlign="center" style={{ color: "#999" }}>
                  No products found
                </Segment>
              )}
          </>
        )}

        {/* Selected Product */}
        {effectiveProductId && (
          <>
            <Header as="h4" dividing>
              Selected Product
              {needsSearch && (
                <Button
                  size="mini"
                  basic
                  floated="right"
                  style={{ marginTop: "-4px" }}
                  onClick={() => {
                    setSelectedProductId(undefined);
                    setSelectedProductTitle("");
                    setAsins([]);
                    setAsin("");
                    setCompetitorListingTitle("");
                    setCompetitorListingTitleTouched(false);
                    setSubmitted(false);
                    setSuccess(false);
                  }}
                >
                  Change
                </Button>
              )}
            </Header>
            <Table definition compact>
              <Table.Body>
                <Table.Row>
                  <Table.Cell width={4}>Product ID</Table.Cell>
                  <Table.Cell>{effectiveProductId}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Product Title</Table.Cell>
                  <Table.Cell>{effectiveProductTitle}</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>

            {/* Existing ASINs */}
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              ASINs Linked to This Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Review currently linked ASINs before adding a new one.
            </p>

            {loadError && (
              <Message negative>
                <p>{loadError}</p>
              </Message>
            )}

            {loadingAsins ? (
              <Segment style={{ minHeight: "60px" }}>
                <Dimmer active inverted>
                  <Loader size="small">Loading ASINs…</Loader>
                </Dimmer>
              </Segment>
            ) : asins.length === 0 ? (
              <Segment textAlign="center" style={{ color: "#999" }}>
                No ASINs linked yet
              </Segment>
            ) : (
              <List divided relaxed celled>
                {asins.map((asinGroup) => (
                  <List.Item key={asinGroup.asin}>
                    <List.Content>
                      <List.Header>
                        <Label color="blue" horizontal>
                          ASIN
                        </Label>
                        {asinGroup.asin}
                        <span
                          style={{
                            marginLeft: "1.5em",
                            color: "#888",
                            fontWeight: "normal",
                            fontSize: "0.85em",
                          }}
                        >
                          <span>
                            Created:{" "}
                            {asinGroup.created_at
                              ? renderDate(asinGroup.created_at)
                              : "—"}
                          </span>
                          <span style={{ margin: "0 0.5em" }}>·</span>
                          <span>By: {asinGroup.created_by ?? "—"}</span>
                        </span>
                      </List.Header>
                      {asinGroup.listings.length > 0 ? (
                        <List.List style={{ marginTop: "0.5em" }}>
                          {asinGroup.listings.map((listing) => (
                            <List.Item key={listing.listing_id}>
                              <List.Content>
                                <List.Description>
                                  <Label basic horizontal size="tiny">
                                    MSKU
                                  </Label>
                                  {listing.msku ?? "—"}
                                  <span
                                    style={{ marginLeft: "1em", color: "#666" }}
                                  >
                                    {listing.listing_title ?? "—"}
                                  </span>
                                </List.Description>
                              </List.Content>
                            </List.Item>
                          ))}
                        </List.List>
                      ) : (
                        <List.Description
                          style={{ color: "#999", marginTop: "0.5em" }}
                        >
                          No associated listings
                        </List.Description>
                      )}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}

            {/* Add new competitor ASIN */}
            {!success && (
              <>
                <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
                  Add Competitor ASIN Information
                </Header>
                <p style={{ color: "#666", fontSize: "0.9em" }}>
                  Enter the competitor ASIN and listing title to link to this
                  product.
                </p>

                {submitError && (
                  <Message negative>
                    <p>{submitError}</p>
                  </Message>
                )}

                <Form>
                  <Form.Field required>
                    <label>New ASIN</label>
                    <Form.Input
                      placeholder="Enter ASIN"
                      value={asin}
                      onChange={(e) => {
                        // Trims a pasted value before the 10-character limit
                        setAsin(normalizeIdentifierInput("asin", e.target.value));
                        setSubmitError(null);
                      }}
                      disabled={isSubmitting}
                    />
                    {submitted && !asinTrimmed && (
                      <Label basic color="red" pointing>
                        ASIN is required
                      </Label>
                    )}
                    {submitted && asinTrimmed && !isValidFormat && (
                      <Label basic color="red" pointing>
                        ASIN must be exactly 10 alphanumeric characters
                      </Label>
                    )}
                    {asinTrimmed && isValidFormat && isDuplicate && (
                      <Label basic color="red" pointing>
                        This ASIN is already linked to this product.
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field required>
                    <label>Competitor Listing Title</label>
                    <Form.Input
                      placeholder="Enter competitor listing title"
                      value={competitorListingTitle}
                      onChange={(e) => {
                        setCompetitorListingTitleTouched(true);
                        setCompetitorListingTitle(e.target.value);
                      }}
                      disabled={isSubmitting}
                    />
                    {submitted && !competitorListingTitleTrimmed && (
                      <Label basic color="red" pointing>
                        Competitor Listing Title is required
                      </Label>
                    )}
                  </Form.Field>
                  <Form.Field>
                    <label>Created On</label>
                    <Form.Input disabled value={createdOn} readOnly />
                  </Form.Field>
                  <Form.Field>
                    <label>Created By</label>
                    <Form.Input disabled value={createdBy} readOnly />
                  </Form.Field>
                </Form>
              </>
            )}

            {success && (
              <Message positive>
                <Message.Header>ASIN Linked Successfully!</Message.Header>
                <p>
                  Competitor ASIN <strong>{asinTrimmed}</strong> has been linked
                  to this product.
                </p>
              </Message>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(success)}>
          {success ? "Close" : "Cancel"}
        </Button>
        {effectiveProductId && !success && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={!canSubmit || loadingAsins}
            loading={isSubmitting}
          >
            Add ASIN
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
